import type { StaffType } from "odyssey-shared";
import type { StaffClient, StaffRow } from "./staff-client.ts";

/**
 * The page's read of the staff accounts the lead's Staff tab shows (ADR-112 decision 5.5, P1.9-T207), owned by the page on
 * the desk's pattern: one read when the tab opens and one after each change, the account chosen, and a designation sent
 * for it. An answer to a read the tab has since left is dropped. Nothing is asked on a timer.
 */

/**
 * Where the list stands: not read; reading; read; or a limit, a session or a role that ended, or a failure.
 */
export type StaffState = "idle" | "reading" | "ok" | "failed" | "rate" | "session" | "forbidden";

/**
 * Why a change was not saved: the type is no longer one the account holds, nobody holds the family now, the roles do not
 * allow it, the session ended, a limit, or a failure.
 */
export type StaffFault = "refused" | "missing" | "forbidden" | "session" | "rate" | "failed";

/**
 * What the tab shows: where the read stands, the rows (kept while they are read again), whether the gateway left staff out, the family chosen, whether a change
 * is out, what the last change saved and why the last one was not.
 */
export interface StaffSnapshot {
  readonly state: StaffState;
  readonly rows: readonly StaffRow[];
  readonly more: boolean;
  readonly selected: string | null;
  readonly saving: boolean;
  readonly saved: { readonly family: string; readonly type: StaffType | null } | null;
  readonly error: StaffFault | null;
}

const IDLE: StaffSnapshot = Object.freeze({ state: "idle", rows: [], more: false, selected: null, saving: false, saved: null, error: null });

/**
 * The model.
 */
export class StaffModel {
  private readonly listeners = new Set<() => void>();
  private snapshot: StaffSnapshot = IDLE;
  private asked = 0;
  private readonly flights = new Set<Promise<void>>();

  /**
   * @param client The two requests.
   */
  public constructor(private readonly client: StaffClient) {}

  /**
   * Hears that the snapshot changed. Works unbound, as React calls it.
   *
   * @param listener Called on each change.
   * @returns Stops hearing.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the tab shows now. Works unbound, as React calls it.
   *
   * @returns The snapshot, the same object until something changes.
   */
  public readonly getSnapshot = (): StaffSnapshot => this.snapshot;

  /**
   * The tab opened: reads the list.
   */
  public readonly open = (): void => {
    this.read();
  };

  /**
   * Reads the list again, keeping the rows shown meanwhile.
   */
  public readonly refresh = (): void => {
    this.read();
  };

  /**
   * The tab closed: the selection and the last answer are forgotten and a read still out is dropped.
   */
  public readonly close = (): void => {
    this.asked += 1;
    this.set({ ...this.snapshot, state: this.snapshot.rows.length === 0 ? "idle" : this.snapshot.state === "reading" ? "ok" : this.snapshot.state, selected: null, saved: null, error: null });
  };

  /**
   * Chooses an account, or none, and forgets the last answer.
   *
   * @param family The family as listed, or null.
   */
  public readonly select = (family: string | null): void => {
    this.set({ ...this.snapshot, selected: family, saved: null, error: null });
  };

  /**
   * Designates the type the chosen account's badge shows, or clears the designation with null. Nothing is sent with no
   * account chosen or while a change is out.
   *
   * @param type The type, or null.
   */
  public readonly designate = (type: StaffType | null): void => {
    const family = this.snapshot.selected;
    if (family === null || this.snapshot.saving) {
      return;
    }
    this.set({ ...this.snapshot, saving: true, saved: null, error: null });
    this.track(this.client.designate(family, type).then((reply) => {
      if (reply.kind === "ok") {
        this.set({ ...this.snapshot, saving: false, saved: { family, type }, error: null });
        this.read();
        return;
      }
      this.set({ ...this.snapshot, saving: false, error: reply.kind });
      if (reply.kind === "refused" || reply.kind === "missing") {
        this.read();
      }
    }));
  };

  /**
   * Settles once the last request asked has been answered.
   *
   * @returns A promise that settles then.
   */
  public async idle(): Promise<void> {
    while (this.flights.size > 0) {
      await Promise.all([...this.flights]);
    }
  }

  /**
   * Keeps a request in flight until it settles, which `idle` waits for.
   *
   * @param flight The request.
   */
  private track(flight: Promise<void>): void {
    this.flights.add(flight);
    void flight.finally(() => this.flights.delete(flight));
  }

  /**
   * Reads the list.
   */
  private read(): void {
    this.asked += 1;
    const asked = this.asked;
    this.set({ ...this.snapshot, state: "reading" });
    this.track(this.client.list().then((reply) => {
      if (asked !== this.asked) {
        return;
      }
      if (reply.kind === "ok") {
        const rows = reply.value.rows;
        const selected = this.snapshot.selected !== null && rows.some((row) => row.family === this.snapshot.selected) ? this.snapshot.selected : null;
        this.set({ ...this.snapshot, state: "ok", rows, more: reply.value.more, selected });
        return;
      }
      this.set({ ...this.snapshot, state: reply.kind === "refused" || reply.kind === "missing" ? "failed" : reply.kind });
    }));
  }

  /**
   * Keeps a snapshot and tells the listeners.
   *
   * @param next The snapshot.
   */
  private set(next: StaffSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
